import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Cable, ClipboardCheck, FileText, Camera, Ruler, Shield, Users, Building, 
  Home as HomeIcon, CheckCircle2, XCircle, AlertCircle, Smartphone, Lock,
  TrendingUp, Zap, Download, Eye, Clock, Target, ArrowRight, Check, X, 
  Mail, Phone, Sparkles
} from 'lucide-react';
import {
  FadeInWhenVisible, StaggerContainer, StaggerItem, TiltCard, MagneticButton
} from '../components/AnimatedUI';

const ModernSections = ({ demoForm, setDemoForm, pilotForm, setPilotForm, handleDemoSubmit, handlePilotSubmit, roiCalculator }) => {
  return (
    <>
      {/* Problem Section */}
      <section className="py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-red-50 text-red-600 border-red-200">Das Problem</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Glasfaser-Projekte scheitern selten an der Arbeit –{' '}
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  sondern an fehlender Dokumentation.
                </span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Viele NE3- und NE4-Projekte werden heute noch mit WhatsApp, Excel, Papierprotokollen und unstrukturierten Fotos organisiert.
              </p>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Camera, title: 'Fotos verstreut', desc: 'Bilder liegen auf verschiedenen Handys und in WhatsApp-Chats' },
              { icon: Ruler, title: 'Aufmaße unvollständig', desc: 'Mengen fehlen oder wurden nicht erfasst' },
              { icon: AlertCircle, title: 'Monteure unsicher', desc: 'Was genau dokumentiert werden muss, ist unklar' },
              { icon: Clock, title: 'Verzögerungen', desc: 'Auftraggeber fordern Nachweise nach, Abnahmen verzögern sich' },
              { icon: XCircle, title: 'Messprotokolle fehlen', desc: 'Technische Abnahme kann nicht erfolgen' },
              { icon: FileText, title: 'Rechnungen blockiert', desc: 'Ohne vollständige Doku keine Abrechnung' },
              { icon: Eye, title: 'Kein Status', desc: 'Niemand weiß, welcher Anschluss fertig ist' },
              { icon: Shield, title: 'Haftungsrisiken', desc: 'Keine Nachweise bei Gewährleistungsfällen' }
            ].map((problem, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="group h-full"
                >
                  <Card className="h-full hover:shadow-2xl transition-all duration-300 border-gray-200 group-hover:border-red-300 bg-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-2xl group-hover:bg-red-500/10 transition-all" />
                    <CardHeader>
                      <motion.div
                        whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.5 } }}
                        className="h-14 w-14 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center mb-3 group-hover:from-red-100 group-hover:to-red-200 transition-all"
                      >
                        <problem.icon className="h-7 w-7 text-red-500" />
                      </motion.div>
                      <CardTitle className="text-lg">{problem.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm leading-relaxed">{problem.desc}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Solution Section - Animated Workflow */}
      <section id="workflow" className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(6, 182, 212, 0.15) 0%, transparent 50%),
                              radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-cyan-500/10 text-cyan-300 border-cyan-500/30">Die Lösung</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                Ein geführter Ablauf für{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  jeden Anschluss.
                </span>
              </h2>
              <p className="text-xl text-gray-400 leading-relaxed">
                AnschlussPilot führt jeden Auftrag Schritt für Schritt durch den richtigen Prozess.
              </p>
            </div>
          </FadeInWhenVisible>

          <div className="max-w-4xl mx-auto">
            {[
              'Auftrag anlegen',
              'Auftragstyp wählen: NE3, NE4, Kombi, Begehung',
              'Gebäude / Wohnungen einrichten',
              'Begehung durchführen',
              'Eigentümer unterschreiben lassen',
              'Arbeiten ausführen',
              'Pflichtfotos erfassen',
              'Aufmaß und Messung dokumentieren',
              'Abnahme vorbereiten',
              'PDF an Auftraggeber senden'
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                className="flex items-start mb-6 group"
              >
                <div className="flex-shrink-0 relative">
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 text-white rounded-2xl flex items-center justify-center font-bold text-xl shadow-lg shadow-cyan-500/50 relative z-10"
                  >
                    {idx + 1}
                  </motion.div>
                  {idx < 9 && (
                    <div className="absolute top-14 left-1/2 -translate-x-1/2 w-0.5 h-12 bg-gradient-to-b from-cyan-500/50 to-cyan-500/0" />
                  )}
                </div>
                <motion.div 
                  whileHover={{ x: 8 }}
                  className="ml-6 flex-1 bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 group-hover:border-cyan-500/50 group-hover:bg-white/10 transition-all"
                >
                  <p className="text-lg font-semibold text-white">{step}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Auftragstypen Section - Modern Cards */}
      <section id="funktionen" className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">Auftragstypen</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Für NE3, NE4 und komplette{' '}
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Hausanschluss-Prozesse.
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                title: 'NE3 / Kategorie 3', 
                desc: 'Von der Straße bis zum Haus',
                icon: Cable, 
                color: 'from-blue-500 to-blue-600',
                bgGlow: 'bg-blue-500/10',
                items: ['Trasse', 'Graben / Fräsen', 'Leerrohr', 'Kabel', 'Hauseinführung', 'Oberfläche', 'Aufmaß Tiefbau', 'Fotodokumentation']
              },
              { 
                title: 'NE4 / Kategorie 4', 
                desc: 'Vom Hausverteiler bis zur Wohnung',
                icon: Building, 
                color: 'from-cyan-500 to-cyan-600',
                bgGlow: 'bg-cyan-500/10',
                items: ['Begehung', 'Leitungsweg', 'Steigleitung', 'Wohnungszuführung', 'Glasfaserdose', 'Spleißen', 'Messung', 'Wohnungsabnahme']
              },
              { 
                title: 'Kombi NE3 + NE4', 
                desc: 'Straße bis Wohnung in einem Ablauf',
                icon: Zap, 
                color: 'from-emerald-500 to-emerald-600',
                bgGlow: 'bg-emerald-500/10',
                items: ['Gesamter Prozess', 'Getrennte Dokumentation', 'Getrenntes Aufmaß', 'Gemeinsamer PDF-Nachweis']
              },
              { 
                title: 'Begehung', 
                desc: 'Vor-Ort-Prüfung mit Unterschrift',
                icon: ClipboardCheck, 
                color: 'from-orange-500 to-orange-600',
                bgGlow: 'bg-orange-500/10',
                items: ['Eigentümer', 'Hausverwaltung', 'Leitungsweg', 'Technikraum', 'HÜP/APL', 'Freigabe']
              },
              { 
                title: 'Nacharbeit', 
                desc: 'Mängel sauber abarbeiten',
                icon: AlertCircle, 
                color: 'from-red-500 to-red-600',
                bgGlow: 'bg-red-500/10',
                items: ['Grund dokumentieren', 'Foto erfassen', 'Zuständigkeit', 'Status verfolgen', 'Abschlussnachweis']
              }
            ].map((type, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ y: -12, transition: { duration: 0.3 } }}
                  className="group h-full"
                >
                  <Card className="h-full overflow-hidden border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 relative bg-white">
                    {/* Gradient glow */}
                    <div className={`absolute -top-20 -right-20 w-40 h-40 ${type.bgGlow} rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500`} />
                    
                    <CardHeader className="relative">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 10 }}
                        transition={{ type: "spring", stiffness: 400 }}
                        className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-4 shadow-lg`}
                      >
                        <type.icon className="h-8 w-8 text-white" />
                      </motion.div>
                      <CardTitle className="text-2xl">{type.title}</CardTitle>
                      <CardDescription className="text-base">{type.desc}</CardDescription>
                    </CardHeader>
                    <CardContent className="relative">
                      <ul className="space-y-3">
                        {type.items.map((item, i) => (
                          <motion.li 
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.05 * i }}
                            className="flex items-center text-gray-700"
                          >
                            <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0">
                              <Check className="h-3 w-3 text-green-600" />
                            </div>
                            <span className="text-sm">{item}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Roles Section - 3D Interactive Cards */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-cyan-500/10 text-cyan-300 border-cyan-500/30">Rollen</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                Jede Rolle sieht genau das,{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  was sie braucht.
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Auftragnehmer / Bauleiter',
                color: 'from-blue-500 to-cyan-500',
                items: ['Projekte einrichten', 'Gebäude und Wohnungen anlegen', 'Monteure zuweisen', 'Pflichtdokumentation definieren', 'Aufmaß prüfen', 'Abnahmen vorbereiten', 'PDFs exportieren', 'Cockpit für offene Punkte']
              },
              {
                icon: Smartphone,
                title: 'Monteur',
                color: 'from-cyan-500 to-blue-500',
                items: ['Heutige Termine sehen', 'Wohnung öffnen', 'Nächster Schritt wird angezeigt', 'Pflichtfoto aufnehmen', 'Aufmaß erfassen', 'Messung dokumentieren', '"Kann nicht weiter" melden', 'Fertigmeldung nur bei vollständiger Doku'],
                highlight: true
              },
              {
                icon: Shield,
                title: 'Auftraggeber / Prüfer',
                color: 'from-emerald-500 to-cyan-500',
                items: ['Fortschritt sehen', 'Freigegebene Fotos prüfen', 'Messprotokolle sehen', 'PDF-Dokumentation herunterladen', 'Abnahmen prüfen', 'Nacharbeiten anfordern']
              }
            ].map((role, idx) => (
              <FadeInWhenVisible key={idx} delay={idx * 0.15}>
                <TiltCard className={`relative h-full ${role.highlight ? 'lg:scale-105' : ''}`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 rounded-3xl backdrop-blur-md border border-white/10" />
                  <div className="relative p-8 h-full">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`h-20 w-20 rounded-3xl bg-gradient-to-br ${role.color} flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/30`}
                    >
                      <role.icon className="h-10 w-10 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-white mb-6">{role.title}</h3>
                    <ul className="space-y-3">
                      {role.items.map((item, i) => (
                        <li key={i} className="flex items-start text-gray-300">
                          <CheckCircle2 className="h-5 w-5 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </TiltCard>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* Pflichtdokumentation Section */}
      <section className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-red-50 text-red-600 border-red-200">Pflichtdokumentation</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Abschluss nur, wenn{' '}
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  alles vollständig ist.
                </span>
              </h2>
              <p className="text-xl text-gray-600">
                AnschlussPilot blockiert die Fertigmeldung, wenn Pflichtfotos, Aufmaß, Messprotokolle, Begehungsfreigabe oder Abnahme fehlen.
              </p>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Camera, title: 'Foto Hauseinführung fehlt' },
              { icon: Camera, title: 'Glasfaserdose nach Montage fehlt' },
              { icon: FileText, title: 'Messprotokoll fehlt' },
              { icon: FileText, title: 'Aufmaß Kabelmeter fehlt' },
              { icon: FileText, title: 'Eigentümer-Unterschrift fehlt' },
              { icon: AlertCircle, title: 'Nacharbeit noch offen' }
            ].map((item, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ scale: 1.03, x: 4 }}
                  className="bg-white border-l-4 border-l-red-500 rounded-r-2xl p-5 shadow-md hover:shadow-xl transition-all flex items-center space-x-4"
                >
                  <div className="h-12 w-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-6 w-6 text-red-500" />
                  </div>
                  <p className="font-semibold text-gray-900">{item.title}</p>
                  <Lock className="h-5 w-5 text-red-400 ml-auto" />
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <FadeInWhenVisible>
            <div className="text-center bg-gradient-to-r from-cyan-500 to-blue-500 rounded-3xl p-10 shadow-2xl shadow-cyan-500/30">
              <p className="text-2xl text-white font-bold">
                So entstehen prüfbare Nachweise statt unvollständiger WhatsApp-Fotos.
              </p>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Begehungsprotokoll Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeInWhenVisible direction="left">
              <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200">Begehung</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight leading-tight">
                Begehung mit Eigentümer-Freigabe und{' '}
                <span className="bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">
                  digitaler Unterschrift.
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Vor der Montage kann die Begehung digital dokumentiert werden. Leitungsweg, Technikraum, HÜP/APL, Bohrungen, Hindernisse und offene Punkte werden erfasst.
              </p>
              <StaggerContainer className="grid grid-cols-2 gap-4">
                {[
                  'Begehungs-Checkliste', 'Pflichtfotos', 'Leitungsweg', 'HÜP / APL',
                  'Technikraum', 'Offene Punkte', 'Digitale Unterschrift', 'PDF-Protokoll'
                ].map((item, idx) => (
                  <StaggerItem key={idx}>
                    <div className="flex items-center space-x-2 group">
                      <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-md shadow-emerald-500/30 group-hover:scale-110 transition-transform">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </FadeInWhenVisible>

            <FadeInWhenVisible direction="right">
              <motion.div whileHover={{ scale: 1.02 }} className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-cyan-400 to-emerald-400 rounded-3xl blur-2xl opacity-30" />
                <img 
                  src="https://images.unsplash.com/photo-1621905253185-95614217f357?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBzbWFydHBob25lfGVufDB8fHx8MTc4MDI1NDU5OXww&ixlib=rb-4.1.0&q=85"
                  alt="Worker with smartphone"
                  className="relative rounded-3xl shadow-2xl"
                />
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-gray-100"
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">Begehung freigegeben</p>
                      <p className="text-xs text-gray-500">Eigentümer-Unterschrift erhalten</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Aufmaß Section */}
      <section className="py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200">Aufmaß</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Aufmaß direkt dort erfassen,{' '}
                <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  wo die Arbeit passiert.
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {[
              { title: 'NE3-Aufmaß', items: ['Grabenmeter', 'Fräsmeter', 'Leerrohrmeter', 'Kabelmeter', 'Oberfläche', 'Hauseinführung', 'Abdichtung'], color: 'blue' },
              { title: 'NE4-Aufmaß', items: ['Glasfaserkabel', 'Kabelkanal', 'Steigleitung', 'Wohnungszuführung', 'Dosen', 'Spleiße', 'Bohrungen', 'Brandschutzdurchführungen'], color: 'cyan' }
            ].map((section, idx) => (
              <FadeInWhenVisible key={idx} delay={idx * 0.2}>
                <TiltCard>
                  <Card className="h-full p-2 border-2 border-gray-100 hover:border-cyan-300 transition-all overflow-hidden">
                    <CardHeader>
                      <div className={`h-2 w-20 rounded-full bg-gradient-to-r ${section.color === 'blue' ? 'from-blue-500 to-blue-300' : 'from-cyan-500 to-cyan-300'} mb-4`} />
                      <CardTitle className="text-3xl">{section.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3">
                        {section.items.map((item, i) => (
                          <motion.div
                            key={i}
                            whileHover={{ scale: 1.05 }}
                            className="bg-gray-50 rounded-xl p-3 text-sm font-medium text-gray-700 hover:bg-cyan-50 hover:text-cyan-700 transition-colors"
                          >
                            {item}
                          </motion.div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TiltCard>
              </FadeInWhenVisible>
            ))}
          </div>

          <FadeInWhenVisible>
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-3xl p-8 flex items-start space-x-4">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-lg mb-1">Video-/Scan-Aufmaß vorbereitet</p>
                <p className="text-gray-700">Mengen aus Video oder Scan als Vorschlag, mit menschlicher Prüfung vor Übernahme.</p>
              </div>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Blockierung Section */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-orange-500/10 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-orange-500/10 text-orange-300 border-orange-500/30">Blockierung</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                Wenn der Monteur nicht weiterkommt,{' '}
                <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                  geht nichts verloren.
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer className="grid md:grid-cols-3 gap-4">
            {['Kein Zutritt', 'Eigentümer nicht erreichbar', 'Leitungsweg unklar', 'Bohrung nicht möglich', 'Material fehlt', 'Messung nicht bestanden', 'Auftraggeberfreigabe fehlt', 'Sicherheitsproblem', 'Sonstiger Grund'].map((grund, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -4 }}
                  className="bg-white/5 backdrop-blur-md border border-white/10 hover:border-orange-500/50 rounded-2xl p-5 flex items-center space-x-3 transition-all group"
                >
                  <div className="h-10 w-10 rounded-lg bg-orange-500/20 flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
                    <AlertCircle className="h-5 w-5 text-orange-400" />
                  </div>
                  <p className="font-semibold text-white">{grund}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* PDF Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-red-50 text-red-600 border-red-200">PDF Dokumentation</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Jeder Auftrag wird{' '}
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  PDF-tauglich dokumentiert.
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer className="grid md:grid-cols-3 lg:grid-cols-3 gap-6">
            {[
              'Begehungsprotokoll', 'NE3-Hausanschluss-Dokumentation', 'NE4-Wohnungsdokumentation',
              'Gebäude-Gesamtdokumentation', 'Aufmaß-PDF', 'Messprotokoll',
              'Nacharbeitsprotokoll', 'Abnahmeprotokoll', 'Projekt-Gesamtdokumentation'
            ].map((pdf, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ y: -8, rotateY: 5 }}
                  style={{ transformStyle: 'preserve-3d' }}
                  className="group relative bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-red-200 cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform">
                        <FileText className="h-7 w-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-white border-2 border-red-500 flex items-center justify-center">
                        <Download className="h-3 w-3 text-red-500" />
                      </div>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{pdf}</p>
                      <p className="text-xs text-gray-500 mt-1">PDF Export</p>
                    </div>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Cockpit Section */}
      <section className="py-32 bg-gradient-to-b from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">Cockpit</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Alles im Blick:{' '}
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Fortschritt, Qualität und Abrechnungsbereitschaft.
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Fortschritt', value: '78%', color: 'from-cyan-500 to-blue-500', icon: TrendingUp },
              { label: 'Gebäude', value: '24', color: 'from-gray-500 to-gray-600', icon: Building },
              { label: 'Wohnungen', value: '156', color: 'from-gray-500 to-gray-600', icon: HomeIcon },
              { label: 'Abgenommen', value: '142', color: 'from-green-500 to-emerald-500', icon: CheckCircle2 },
              { label: 'Fotos fehlen', value: '8', color: 'from-red-500 to-red-600', icon: Camera },
              { label: 'Messprotokolle fehlen', value: '3', color: 'from-red-500 to-red-600', icon: FileText },
              { label: 'Offene Aufmaße', value: '5', color: 'from-orange-500 to-orange-600', icon: FileText },
              { label: 'Nacharbeiten', value: '6', color: 'from-orange-500 to-orange-600', icon: AlertCircle },
              { label: 'Kein Zutritt', value: '2', color: 'from-orange-500 to-orange-600', icon: Lock },
              { label: 'Zur Abnahme bereit', value: '12', color: 'from-blue-500 to-cyan-500', icon: Target },
              { label: 'Abrechnungsbereit', value: '134', color: 'from-green-500 to-emerald-500', icon: CheckCircle2 },
              { label: 'Live Status', value: 'ON', color: 'from-cyan-500 to-blue-500', icon: Sparkles },
            ].map((stat, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -4 }}
                  className="bg-white rounded-2xl p-5 shadow-md hover:shadow-xl border border-gray-100 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-md`}>
                      <stat.icon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ROI Calculator */}
      {roiCalculator}

      {/* Comparison Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">Vergleich</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Nicht noch eine Aufgaben-App.{' '}
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Eine Prozess-App für Glasfaser.
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <FadeInWhenVisible>
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="p-5 text-left font-bold text-gray-900">Methode</th>
                      <th className="p-5 text-center font-bold text-gray-900">Strukturiert</th>
                      <th className="p-5 text-center font-bold text-gray-900">Pflichtdoku</th>
                      <th className="p-5 text-center font-bold text-gray-900">NE3/NE4</th>
                      <th className="p-5 text-center font-bold text-gray-900">Begehung</th>
                      <th className="p-5 text-center font-bold text-gray-900">Aufmaß</th>
                      <th className="p-5 text-center font-bold text-gray-900">PDF</th>
                      <th className="p-5 text-center font-bold text-gray-900">Rollen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'WhatsApp', values: [false, false, false, false, false, false, false] },
                      { name: 'Excel', values: [false, false, false, false, false, false, false] },
                      { name: 'Papier', values: [false, false, false, false, false, false, false] },
                      { name: 'Allgemeine Projektsoftware', values: [true, false, false, false, false, false, false] },
                      { name: 'AnschlussPilot', values: [true, true, true, true, true, true, true], highlight: true }
                    ].map((row, idx) => (
                      <motion.tr
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                        className={row.highlight 
                          ? 'bg-gradient-to-r from-cyan-50 to-blue-50' 
                          : 'hover:bg-gray-50 transition-colors'
                        }
                      >
                        <td className="p-5 font-bold text-gray-900 border-t border-gray-100">
                          {row.highlight && <span className="inline-block w-2 h-2 bg-cyan-500 rounded-full mr-2 animate-pulse" />}
                          {row.name}
                        </td>
                        {row.values.map((val, i) => (
                          <td key={i} className="p-5 text-center border-t border-gray-100">
                            {val ? (
                              <div className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 shadow-md shadow-green-500/30">
                                <Check className="h-5 w-5 text-white" />
                              </div>
                            ) : (
                              <div className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-100">
                                <X className="h-4 w-4 text-gray-400" />
                              </div>
                            )}
                          </td>
                        ))}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Pilotkunden Section */}
      <section id="pilot" className="py-32 bg-gradient-to-br from-cyan-50 via-blue-50 to-cyan-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/20 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-cyan-500 text-white px-4 py-2 text-base shadow-lg shadow-cyan-500/30">
                <Sparkles className="h-4 w-4 mr-2 inline" />
                Jetzt starten
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Jetzt als{' '}
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Pilotprojekt
                </span>{' '}
                testen.
              </h2>
              <p className="text-xl text-gray-700">
                Wir suchen ausgewählte Auftragnehmer, NE4-Firmen und Projektpartner.
              </p>
            </div>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.2}>
            <Card className="shadow-2xl border-2 border-cyan-100 bg-white/80 backdrop-blur-xl">
              <CardContent className="pt-8">
                <form onSubmit={handlePilotSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" required value={pilotForm.name} onChange={(e) => setPilotForm({ ...pilotForm, name: e.target.value })} className="mt-1 h-12" />
                    </div>
                    <div>
                      <Label htmlFor="company">Unternehmen *</Label>
                      <Input id="company" required value={pilotForm.company} onChange={(e) => setPilotForm({ ...pilotForm, company: e.target.value })} className="mt-1 h-12" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input id="email" type="email" required value={pilotForm.email} onChange={(e) => setPilotForm({ ...pilotForm, email: e.target.value })} className="mt-1 h-12" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefon</Label>
                      <Input id="phone" value={pilotForm.phone} onChange={(e) => setPilotForm({ ...pilotForm, phone: e.target.value })} className="mt-1 h-12" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Nachricht</Label>
                    <Textarea id="message" value={pilotForm.message} onChange={(e) => setPilotForm({ ...pilotForm, message: e.target.value })} rows={4} className="mt-1" placeholder="Erzählen Sie uns mehr über Ihr Projekt..." />
                  </div>
                  <MagneticButton>
                    <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-lg shadow-xl shadow-cyan-500/30 group">
                      Pilotprojekt anfragen
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </MagneticButton>
                </form>
              </CardContent>
            </Card>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="preise" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">Preise</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Transparente{' '}
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Preise
                </span>
              </h2>
              <p className="text-xl text-gray-600">Wählen Sie das passende Paket für Ihr Team</p>
            </div>
          </FadeInWhenVisible>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Starter', subtitle: 'Für kleine Teams', price: 'ab 99 €', period: '/Monat', features: ['Bis 5 Nutzer', 'Bis 3 Projekte', 'Pflichtdokumentation', 'Fotos', 'Aufmaß', 'PDF-Export'], cta: 'Starter wählen', highlight: false },
              { name: 'Pro', subtitle: 'Für wachsende Auftragnehmer', price: 'ab 249 €', period: '/Monat', features: ['Bis 20 Nutzer', 'Unbegrenzte Wohnungen', 'NE3 / NE4 Workflows', 'Begehungsprotokoll', 'Auftraggeberansicht', 'Nacharbeiten', 'Abnahmen'], cta: 'Pro wählen', highlight: true },
              { name: 'Business', subtitle: 'Für größere Projekte', price: 'Auf Anfrage', period: '', features: ['Mehrere Auftraggeber', 'Mehrere Organisationen', 'API', 'Scan-/Video-Aufmaß', 'Rollen & Rechte', 'Individuelle Workflows', 'Private Instanz möglich'], cta: 'Kontakt aufnehmen', highlight: false }
            ].map((plan, idx) => (
              <FadeInWhenVisible key={idx} delay={idx * 0.15}>
                <motion.div
                  whileHover={{ y: -12 }}
                  className={`relative h-full ${plan.highlight ? 'lg:scale-105' : ''}`}
                >
                  {plan.highlight && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-2 text-sm shadow-lg shadow-cyan-500/40">
                        <Sparkles className="h-3 w-3 mr-1 inline" />
                        Empfohlen
                      </Badge>
                    </div>
                  )}
                  <Card className={`h-full overflow-hidden ${plan.highlight ? 'border-2 border-cyan-500 shadow-2xl shadow-cyan-500/20' : 'border-2 border-gray-100 hover:border-gray-200'} transition-all`}>
                    {plan.highlight && <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />}
                    <CardHeader className="pt-8">
                      <p className="text-sm font-semibold text-gray-500 mb-2">{plan.subtitle}</p>
                      <CardTitle className="text-3xl mb-1">{plan.name}</CardTitle>
                      <div className="flex items-baseline mt-4">
                        <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                        <span className="text-gray-500 ml-2">{plan.period}</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-8">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center text-gray-700">
                            <div className={`h-5 w-5 rounded-full ${plan.highlight ? 'bg-gradient-to-br from-cyan-500 to-blue-500' : 'bg-green-500'} flex items-center justify-center mr-3 flex-shrink-0`}>
                              <Check className="h-3 w-3 text-white" />
                            </div>
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <MagneticButton>
                        <Button 
                          variant={plan.highlight ? "default" : "outline"} 
                          className={`w-full ${plan.highlight ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white shadow-lg shadow-cyan-500/30' : ''}`}
                        >
                          {plan.cta}
                        </Button>
                      </MagneticButton>
                    </CardContent>
                  </Card>
                </motion.div>
              </FadeInWhenVisible>
            ))}
          </div>

          <FadeInWhenVisible>
            <p className="text-center text-gray-500 mt-12 text-sm">
              <strong>Hinweis:</strong> Preise können in der Pilotphase individuell angepasst werden.
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">FAQ</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
                Häufig gestellte{' '}
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Fragen
                </span>
              </h2>
            </div>
          </FadeInWhenVisible>

          <FadeInWhenVisible>
            <Accordion type="single" collapsible className="space-y-4">
              {[
                { q: 'Ist AnschlussPilot nur für NE4?', a: 'Nein. AnschlussPilot kann NE3, NE4, Kombiprojekte, Begehungen und Nacharbeiten abbilden.' },
                { q: 'Kann der Monteur die App einfach nutzen?', a: 'Ja. Monteure sehen nur ihre heutigen Aufgaben, Wohnungen, Pflichtfotos, Aufmaß und Meldungen.' },
                { q: 'Kann eine Arbeit abgeschlossen werden, wenn Fotos fehlen?', a: 'Nein. Die App blockiert den Abschluss, wenn Pflichtdokumentation fehlt.' },
                { q: 'Kann der Eigentümer unterschreiben?', a: 'Ja. Begehungsprotokolle können mit digitaler Unterschrift vorbereitet werden.' },
                { q: 'Kann ich PDFs an Auftraggeber senden?', a: 'Ja. Dokumentationen, Aufmaße, Begehungen und Abnahmen können PDF-tauglich exportiert werden.' },
                { q: 'Gibt es Video-Aufmaß?', a: 'Das Modul ist vorbereitet. Mengen aus Video oder Scan können später über eine externe Engine vorgeschlagen werden.' },
                { q: 'Ist AnschlussPilot eine Web-App oder Mobile-App?', a: 'AnschlussPilot ist als mobile Field-App für iOS und Android gedacht. Eine Web-Ansicht für Bauleiter ist zusätzlich möglich.' }
              ].map((faq, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <AccordionItem value={`item-${idx}`} className="bg-white border-2 border-gray-100 hover:border-cyan-200 rounded-2xl px-6 transition-colors">
                    <AccordionTrigger className="text-left font-semibold text-lg hover:text-cyan-600 hover:no-underline py-5">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 text-base pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Demo Form Section */}
      <section id="demo" className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-cyan-500/10 text-cyan-300 border-cyan-500/30">Demo</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                Lassen Sie sich{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  AnschlussPilot
                </span>{' '}
                zeigen
              </h2>
              <p className="text-xl text-gray-400">Persönliche Demo in 30 Minuten – kostenlos und unverbindlich.</p>
            </div>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.2}>
            <Card className="bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl">
              <CardContent className="pt-8">
                <form onSubmit={handleDemoSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="demo-name" className="text-white">Name *</Label>
                      <Input id="demo-name" required value={demoForm.name} onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })} className="mt-1 h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-400" />
                    </div>
                    <div>
                      <Label htmlFor="demo-company" className="text-white">Unternehmen *</Label>
                      <Input id="demo-company" required value={demoForm.company} onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })} className="mt-1 h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-400" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="demo-email" className="text-white">E-Mail *</Label>
                      <Input id="demo-email" type="email" required value={demoForm.email} onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })} className="mt-1 h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-400" />
                    </div>
                    <div>
                      <Label htmlFor="demo-phone" className="text-white">Telefon</Label>
                      <Input id="demo-phone" value={demoForm.phone} onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })} className="mt-1 h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="demo-message" className="text-white">Nachricht</Label>
                    <Textarea id="demo-message" value={demoForm.message} onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })} rows={4} className="mt-1 bg-white/5 border-white/20 text-white placeholder:text-gray-400" placeholder="Was möchten Sie über AnschlussPilot erfahren?" />
                  </div>
                  <MagneticButton>
                    <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-lg shadow-2xl shadow-cyan-500/40 group">
                      Demo anfragen
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </MagneticButton>
                </form>
              </CardContent>
            </Card>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-white py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-baseline mb-4">
                <span className="text-2xl font-bold tracking-tight text-white">Anschluss</span>
                <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Pilot</span>
                <span className="text-2xl font-bold text-cyan-400 ml-0.5">.</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Die mobile Field-App für Glasfaser-Hausanschlüsse.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Produkt</h3>
              <ul className="space-y-2">
                {['Funktionen', 'Workflow', 'Preise', 'Demo'].map((item) => (
                  <li key={item}>
                    <a href={`#${item.toLowerCase()}`} className="text-gray-400 hover:text-cyan-400 transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Unternehmen</h3>
              <ul className="space-y-2">
                <li><a href="#demo" className="text-gray-400 hover:text-cyan-400 transition-colors">Kontakt</a></li>
                <li><Link to="/datenschutz" className="text-gray-400 hover:text-cyan-400 transition-colors" data-testid="footer-datenschutz">Datenschutz</Link></li>
                <li><Link to="/impressum" className="text-gray-400 hover:text-cyan-400 transition-colors" data-testid="footer-impressum">Impressum</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Kontakt</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-400">
                  <Mail className="h-5 w-5 mr-2" />
                  info@anschlusspilot.de
                </li>
                <li className="flex items-center text-gray-400">
                  <Phone className="h-5 w-5 mr-2" />
                  <a href="tel:+41784210422" className="hover:text-cyan-400 transition-colors">+41 78 421 04 22</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} AnschlussPilot. Alle Rechte vorbehalten.
            </p>
            <MagneticButton>
              <Button className="mt-4 md:mt-0 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white shadow-lg shadow-cyan-500/30">
                <a href="#demo">AnschlussPilot Pilotprojekt anfragen</a>
              </Button>
            </MagneticButton>
          </div>
        </div>
      </footer>
    </>
  );
};

export default ModernSections;
